// import './assets/main.css'
import './assets/index.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

import App from './App.vue'
import router from './router'
import * as Sentry from "@sentry/vue";
import { AuthService } from '@/services/auth.ts'
import { UserApi } from '@/services/api/user'
import { DashboardFilters } from '@/services/dashboard-filters'

const app = createApp(App)

Sentry.init({
  app,
  dsn: "https://3e6383c6139a7da1d36025db1352d3d9@o4504754957189120.ingest.us.sentry.io/4508735893274624",
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // tracePropagationTargets: [/^https:\/\/api\.prod\.getrevio\.cloud/],
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  enabled: process.env.NODE_ENV !== 'development',
});


// Global error handler for dynamic import failures
const RELOAD_KEY = 'app_reload_attempted';
const RELOAD_TIMEOUT = 10000; // 10 seconds

window.addEventListener('error', (event) => {
  if (event.message.includes('Failed to fetch dynamically imported module')) {
    // Check if we've already tried reloading recently to prevent infinite loops
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    
    if (!lastReload || (now - parseInt(lastReload)) > RELOAD_TIMEOUT) {
      // Mark that we're attempting a reload
      sessionStorage.setItem(RELOAD_KEY, now.toString());
      
      console.log('Detected outdated chunk, reloading application...');
      // Force a hard reload to clear the cache and get new files
      window.location.reload();
    } else {
      // We've already tried reloading recently, don't loop
      console.error('Failed to load module after reload. Please clear your cache manually.');
      sessionStorage.removeItem(RELOAD_KEY);
    }
  }
});

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate);
app.use(pinia)
app.use(router)

app.mount('#app')


// Listen to updated or cleared tokens
let refreshTimer: NodeJS.Timeout | null = null

const setRefreshTimer = () => {
  if (refreshTimer) clearTimeout(refreshTimer)

  const refreshDuration = AuthService.refreshDuration()
  if (refreshDuration) {
    const timeUntilExpiry = refreshDuration - Date.now()
    refreshTimer = setTimeout(() => {
      AuthService.clearTokens()
      router.push({ name: 'Login' })
    }, timeUntilExpiry)
  }
}

AuthService.subscribe(() => {
  if (!AuthService.isAuthenticated()) {
    if (refreshTimer) clearTimeout(refreshTimer)
    router.push({ name: 'Login' })
  } else {
    setRefreshTimer()
  }
})

// ---- Periodic Role/Permission Refresh ----
let roleRefreshInterval: number | null = null

async function refreshRolesIfNeeded() {
  if (!AuthService.isAuthenticated()) return
  try {
    const info = await UserApi.userInfo()
    const incomingRoles = info.roles || []
    if (incomingRoles.length === 0) return
    const existing = DashboardFilters.getAllRoles()
    const current = DashboardFilters.getCurrentRole()
    // Compare shallow: length or any permission diffs
    let changed = existing.length !== incomingRoles.length
    if (!changed) {
      for (let i=0;i<incomingRoles.length;i++) {
        const a = incomingRoles[i]
        const b = existing.find(r=> r.id===a.id)
        if (!b) { changed = true; break }
        // Compare permissions object shallowly
        const aPerm = a.permissions as any
        const bPerm = b.permissions as any
        const permKeys = Object.keys(aPerm)
        for (const k of permKeys){ if (aPerm[k] !== bPerm[k]) { changed = true; break } }
        // the sign-in scope (branch / RM default) can be changed from another device
        if (!changed && JSON.stringify(a.default_scope ?? null) !== JSON.stringify(b.default_scope ?? null)) changed = true
        if (changed) break
      }
    }
    if (changed) {
      DashboardFilters.saveAllRoles(incomingRoles)
      // try to keep same role id if it still exists, else fallback to first
      const updatedCurrent = incomingRoles.find(r=> r.id === current.id) || incomingRoles[0]
      if (updatedCurrent) {
        DashboardFilters.setRole(updatedCurrent.id)
      }
    }
  } catch (e) {
    // swallow errors silently to avoid user disruption
  }
}

function startRoleRefresh() {
  if (roleRefreshInterval) window.clearInterval(roleRefreshInterval)
  // Run immediately then every 60s
  refreshRolesIfNeeded()
  roleRefreshInterval = window.setInterval(refreshRolesIfNeeded, 60_000)
}

// Start polling after app mounts and user is authenticated
if (AuthService.isAuthenticated()) {
  startRoleRefresh()
}

AuthService.subscribe(() => {
  if (AuthService.isAuthenticated()) {
    startRoleRefresh()
  } else if (roleRefreshInterval) {
    window.clearInterval(roleRefreshInterval)
    roleRefreshInterval = null
  }
})
